import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Users, Building2, CreditCard, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface FunnelData {
  totalSignups: number;
  withOrg: number;
  withJobs: number;
  paying: number;
  conversionRates: {
    signupToOrg: number;
    orgToActive: number;
    activeToPaid: number;
    overall: number;
  };
}

export function ConversionFunnel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FunnelData | null>(null);

  useEffect(() => {
    loadFunnelData();
  }, []);

  const loadFunnelData = async () => {
    try {
      // Total signups (profiles)
      const { count: totalSignups } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true });

      // Users with organizations
      const { count: withOrg } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .not("org_id", "is", null);

      // Organizations with jobs (active usage)
      const { data: orgsWithJobs } = await supabase
        .from("jobs")
        .select("org_id")
        .limit(1000);

      const uniqueActiveOrgs = new Set(orgsWithJobs?.map(j => j.org_id) || []);
      const withJobs = uniqueActiveOrgs.size;

      // Paying customers
      const { count: paying } = await supabase
        .from("organizations")
        .select("*", { count: "exact", head: true })
        .not("stripe_subscription_id", "is", null)
        .eq("subscription_status", "active");

      const total = totalSignups || 0;
      const orged = withOrg || 0;
      const active = withJobs;
      const paid = paying || 0;

      setData({
        totalSignups: total,
        withOrg: orged,
        withJobs: active,
        paying: paid,
        conversionRates: {
          signupToOrg: total > 0 ? Math.round((orged / total) * 100) : 0,
          orgToActive: orged > 0 ? Math.round((active / orged) * 100) : 0,
          activeToPaid: active > 0 ? Math.round((paid / active) * 100) : 0,
          overall: total > 0 ? Math.round((paid / total) * 100) : 0,
        },
      });
    } catch (error) {
      console.error("Error loading funnel data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load funnel data
        </CardContent>
      </Card>
    );
  }

  const funnelSteps = [
    { 
      label: "Sign-ups", 
      value: data.totalSignups, 
      icon: Users,
      color: "bg-blue-500",
      width: 100 
    },
    { 
      label: "Created Org", 
      value: data.withOrg, 
      icon: Building2,
      color: "bg-indigo-500",
      width: data.totalSignups > 0 ? (data.withOrg / data.totalSignups) * 100 : 0,
      conversion: data.conversionRates.signupToOrg
    },
    { 
      label: "Active (Jobs)", 
      value: data.withJobs, 
      icon: TrendingUp,
      color: "bg-purple-500",
      width: data.totalSignups > 0 ? (data.withJobs / data.totalSignups) * 100 : 0,
      conversion: data.conversionRates.orgToActive
    },
    { 
      label: "Paying", 
      value: data.paying, 
      icon: CreditCard,
      color: "bg-green-500",
      width: data.totalSignups > 0 ? (data.paying / data.totalSignups) * 100 : 0,
      conversion: data.conversionRates.activeToPaid
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Conversion Funnel
        </CardTitle>
        <CardDescription>
          User journey from signup to paid customer
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {funnelSteps.map((step, index) => (
            <div key={step.label} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <step.icon className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{step.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold">{step.value}</span>
                  {step.conversion !== undefined && (
                    <span className="text-sm text-muted-foreground">
                      ({step.conversion}% of prev)
                    </span>
                  )}
                </div>
              </div>
              <div className="h-8 bg-muted rounded-lg overflow-hidden">
                <div 
                  className={`h-full ${step.color} transition-all duration-500 rounded-lg flex items-center justify-end pr-2`}
                  style={{ width: `${Math.max(step.width, 5)}%` }}
                >
                  {step.width > 10 && (
                    <span className="text-white text-sm font-medium">
                      {Math.round(step.width)}%
                    </span>
                  )}
                </div>
              </div>
              {index < funnelSteps.length - 1 && (
                <div className="flex justify-center py-1">
                  <div className="w-0.5 h-4 bg-muted-foreground/20" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Overall Conversion */}
        <div className="mt-8 p-4 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center justify-between">
            <span className="font-medium">Overall Conversion Rate</span>
            <span className="text-2xl font-bold text-primary">
              {data.conversionRates.overall}%
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            From signup to paid customer
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
